import { useRouter } from 'next/router'
import { useState, type FC } from 'react'
import { useQueryClient } from 'react-query'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { Header } from 'app/KinerjaShared/components/Header'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import { formatDate } from 'utils/datetime'
import { IS_DEVELOPMENT } from 'app/KinerjaShared/constants/env'
import useTabKsGuru from 'app/KinerjaShared/hooks/useTabKsGuru'
import DialogDeliveryFailed from 'app/KinerjaShared/components/DialogDeliveryFailed'
import usePenetapanTahunanDinas from 'app/KinerjaDinasPage/hooks/usePenetapanTahunanDinas'
import usePenetapanTahunanCountDinas from 'app/KinerjaDinasPage/hooks/usePenetapanTahunanCountDinas'
import { DINAS_PENETAPAN_TAHUNAN_GURU_ENABLED } from 'app/KinerjaDinasPage/constants'
import usePenetapanTahunanKirimDinas from 'app/KinerjaDinasPage/hooks/usePenetapanTahunanKirimDinas'
import { NotFoundError } from 'utils/customError'
import {
  API_GET_COUNT_PENILAIAN_TAHUNAN_DINAS,
  API_GET_LIST_PENILAIAN_TAHUNAN_DINAS,
  API_GET_SUMMARY_PENILAIAN_TAHUNAN_DINAS,
} from 'api/kinerja/kepala-sekolah/dinas/penilaian-tahunan'
import DialogDeliveryConfirmationTahunan from 'app/KinerjaDinasPage/components/DialogDeliveryConfirmationTahunan'
import DialogDeliveryExecutingTahunan from 'app/KinerjaDinasPage/components/DialogDeliveryExecutingTahunan'
import DialogDeliverySuccessTahunan from 'app/KinerjaDinasPage/components/DialogDeliverySuccessTahunan'
import { PENGELOLAAN_KINERJA_KADIN_PATH } from 'app/KinerjaShared/constants/path'
import {
  API_GET_COUNT_PENILAIAN_TAHUNAN_DINAS_TAB_GURU,
  API_GET_LIST_PENILAIAN_TAHUNAN_DINAS_TAB_GURU,
  API_GET_SUMMARY_PENILAIAN_TAHUNAN_DINAS_TAB_GURU,
} from 'api/kinerja/kepala-sekolah/guru/dinas/penilaian-tahunan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { TabFilterRoles } from 'types/kinerja-ks'

type DinasPenetapanTahunanKonfirmasiProps = {}

type AcceptableTabFilterRoles = TabFilterRoles | 'ALL'

const delay = IS_DEVELOPMENT ? 0 : 5000

const hideTabGuru = !DINAS_PENETAPAN_TAHUNAN_GURU_ENABLED

const DinasPenetapanTahunanKonfirmasi: FC<
  DinasPenetapanTahunanKonfirmasiProps
> = ({}) => {
  const router = useRouter()
  const queryClient = useQueryClient()

  const { periodYear } = usePeriodContext()

  const [isOpen, setIsOpen] = useState<{
    name: 'confirmation' | 'loading' | 'success' | 'failed' | null
    count?: number
    type?: AcceptableTabFilterRoles
  }>({ name: null })

  // Tabs
  const {
    render: tabs,
    activeTabName,
    setCounters: setTabCounters,
  } = useTabKsGuru({
    // Initial counters, will be updated after fetching data. Required, to display 0 when currently fetching data.
    initCounters: { KS: 0, GURU: 0 },
    hideTabGuru,
  })
  const onSuccess = () => {
    setIsOpen({ name: null })
    router.back()
  }

  const {
    // Common
    isBothFetched,
    isWithinAllowedRange,
    isClickable,
  } = usePenetapanTahunanCountDinas()

  const {
    // Kepala Sekolah
    itemsKs,
    itemsCountKs,
    refetchListKs,

    // Guru
    itemsGuru,
    itemsCountGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    isListSuccess,
    isListBothFetched,
    totalItems,
    hasItems,

    // Relative to active tab
    relativeItems,
    isRelativeError,
    isRelativeSuccess,
  } = usePenetapanTahunanDinas({
    year: periodYear,
    isAccessible: isWithinAllowedRange,
    activeTabName,
    statusPengiriman: ['BELUM_DIKIRIM'],
    onBothFetched: () => {
      // Set counters for tabs
      setTabCounters({
        KS: itemsCountKs,
        GURU: itemsCountGuru,
      })
    },
  })

  const { mutate } = usePenetapanTahunanKirimDinas({
    includeGuru: !hideTabGuru,
    onSuccess: ({ count }) => {
      // Show success dialog and invalidate queries after 5s delay
      setTimeout(() => {
        // Invalidate queries for Kepala Sekolah
        queryClient.invalidateQueries({
          queryKey: [API_GET_COUNT_PENILAIAN_TAHUNAN_DINAS],
        })

        queryClient.invalidateQueries({
          queryKey: [API_GET_LIST_PENILAIAN_TAHUNAN_DINAS],
        })

        queryClient.invalidateQueries({
          queryKey: [API_GET_SUMMARY_PENILAIAN_TAHUNAN_DINAS],
        })

        // Invalidate queries for Guru
        queryClient.invalidateQueries({
          queryKey: [API_GET_COUNT_PENILAIAN_TAHUNAN_DINAS_TAB_GURU],
        })

        queryClient.invalidateQueries({
          queryKey: [API_GET_LIST_PENILAIAN_TAHUNAN_DINAS_TAB_GURU],
        })

        queryClient.invalidateQueries({
          queryKey: [API_GET_SUMMARY_PENILAIAN_TAHUNAN_DINAS_TAB_GURU],
        })

        setIsOpen({ name: 'success', count })
      }, delay)
    },
    onError: ({ type }) => {
      setIsOpen({ name: 'failed', type })
    },
  })

  // If no data can be delivered, as the user's not supposed to be here, redirect to the list page.
  if (isListBothFetched && totalItems === 0 && isOpen.name === null) {
    router.replace(`${PENGELOLAAN_KINERJA_KADIN_PATH}/penetapan-tahunan`)
    return null
  }

  if (isBothFetched && (!isWithinAllowedRange || !isClickable))
    throw new NotFoundError('', 'Not Found')

  const send = async () => {
    setIsOpen({ name: 'loading' })
    await mutate(itemsKs, itemsGuru)
  }

  return (
    <div>
      <Header
        title="Kirim Predikat Kinerja (Tahunan) ke Pegawai"
        className="mb-9 print:!hidden"
      />

      <section className="bg-white border border-subdued rounded-lg mt-6">
        <div className="px-6 pt-6 flex items-start gap-4">
          {/* Title */}
          <div className="space-y-2.5 flex-1">
            <Text variant="heading-md">Berikut data yang akan dikirim:</Text>
            <Text color="subdued">
              Berisi data Kepala Sekolah{!hideTabGuru && ' dan Guru (jika ada)'}
              . Data yang tidak muncul di sini dapat Anda kirim di pengiriman
              berikutnya.
            </Text>

            <div className="bg-green-5 border border-green-50 rounded p-2 inline-block">
              <div className="flex items-center gap-2">
                <Icon color="success">check_circle_outlined</Icon>
                <Text variant="body" color="subdued">
                  Dikirim mulai dari: {formatDate(new Date())}.
                </Text>
              </div>
            </div>
          </div>

          <div className="flex-none flex flex-row gap-2.5 print:!hidden">
            {isListSuccess && (
              <>
                <Button
                  size="lg"
                  color="white"
                  type="button"
                  className="space-x-1"
                  onClick={() => window.print()}
                >
                  <Icon>print_outlined</Icon> <span>Cetak</span>
                </Button>
                <Button
                  size="lg"
                  disabled={!hasItems}
                  onClick={() => {
                    setIsOpen({ name: 'confirmation' })
                  }}
                >
                  <Icon
                    fontSize="default"
                    color={hasItems ? 'inverse' : 'disabled'}
                    className="mr-1"
                  >
                    mail_outline
                  </Icon>
                  Lanjut kirim ({totalItems})
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        {!hideTabGuru && <>{tabs}</>}

        {/* Table */}
        <Table className={clsx({ 'mt-0': !hideTabGuru })}>
          <TableHeader>
            <TableHeaderItem className="w-2/12">
              <Text variant="body" color="default" className="!font-bold">
                Nama
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Jabatan
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-3/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Predikat Kinerja (Tahunan)
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-5/12 pl-0" />
          </TableHeader>
          <TableBody>
            {/* If loading */}
            {isListLoading && <TableShimmer columns={6} />}

            {/* If error or no data returned */}
            {!isListLoading &&
              (isRelativeError || relativeItems?.length === 0) && (
                <TableRowStates
                  colSpan={6}
                  type={isRelativeError ? 'ERROR' : 'EMPTY'}
                  onClick={() => {
                    activeTabName === 'GURU'
                      ? refetchListGuru()
                      : refetchListKs()
                  }}
                >
                  <>
                    Pastikan terdapat pegawai dengan jabatan tersebut di daerah
                    <br />
                    Anda dan sudah menyelesaikan tahap Pelaksanaan Kinerja.
                  </>
                </TableRowStates>
              )}

            {!isListLoading &&
              isRelativeSuccess &&
              hasItems &&
              relativeItems.map((item, idx) => {
                return (
                  <TableRow key={`ks_${item.npsn}_${idx}`}>
                    <TableRowItem className="align-middle">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.name || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.jabatan || ''}
                      </Text>
                    </TableRowItem>

                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="subheading"
                        color="informational"
                        className="overflow-wrap-anywhere uppercase !text-blue-60"
                      >
                        {item?.predikatKinerjaDinas ||
                          item?.predikatKinerjaRekomendasi}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0" />
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </section>

      <DialogDeliveryConfirmationTahunan
        isOpen={isOpen.name === 'confirmation'}
        onClickCancel={() => setIsOpen({ name: null })}
        onClickConfirm={() => send()}
      />

      <DialogDeliveryExecutingTahunan isOpen={isOpen.name === 'loading'} />

      <DialogDeliverySuccessTahunan
        count={isOpen?.count || 0}
        isOpen={isOpen.name === 'success'}
        onClick={onSuccess}
      />

      <DialogDeliveryFailed
        isOpen={isOpen.name === 'failed'}
        errorType={isOpen.type}
        onClick={onSuccess}
      />
    </div>
  )
}
export default DinasPenetapanTahunanKonfirmasi
